"use client"

import { useEffect, useRef, useState } from "react"
import type { CSSProperties } from "react"
import { createPortal } from "react-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Slider } from "@/components/ui/slider"
import {
  Glasses,
  X,
  Settings,
  ChevronRight,
  ChevronLeft,
  Eye,
  EyeOff,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from "@/lib/icons"
import dynamic from "next/dynamic"
import { vrXRStore, loadVRPlayerModule } from "./vr-xr-store"
import { VRControls } from "./vr-controls"
import { useVRStore } from "@/lib/vr-store"
import { detectVRFromUrl, detectVRFromFilename, getVideoSettingsKey, projectionLabel, stereoLabel } from "@/lib/vr-utils"
import { canAttemptImmersiveXR, checkImmersiveVRSupport, onXRDeviceChange } from "@/lib/vr-xr"
import { activateVRAudio, bypassVRAudio } from "@/lib/vr-audio"
import type { UserScript } from "@/lib/script-store"
import { useTranslations } from "next-intl"

// three.js + the XR runtime (~430 KB gzipped) only load when the VR player is opened,
// not with every page that merely mounts this overlay (see ./vr-xr-store.ts).
const VRPlayer = dynamic(() => loadVRPlayerModule().then((mod) => mod.VRPlayer), {
  ssr: false,
  loading: () => <div className="h-full w-full bg-black" />,
})

interface VROverlayProps {
  /** The existing <video> element controlled by ScriptPlayer */
  videoRef: React.RefObject<HTMLVideoElement | null>
  /** Source URL or local filename — used for detection + settings key */
  contentUrl: string
  /** Original URL or filename before playback proxying/blob conversion */
  sourceId?: string
  /** Script sync delay — passed through to VRControls */
  syncDelay: number
  onSyncDelayChange: (ms: number) => void
  activeScript?: UserScript | null
  onScriptSelect?: (script: UserScript) => void
  onScriptClear?: () => void
  /** Commits a timeline seek to the host's synchronized script/video clock. */
  onSeek?: (seconds: number) => void
  /** Routes play/pause through the host's real toggle (which also starts/stops device
   *  script sync via playDeviceScript/pauseDeviceScript) instead of the video element
   *  being played/paused directly. */
  onPlayPause?: () => void
  /** Fired right as immersive VR is entered — lets a multi-video host (e.g. Gooncave)
   *  know which instance just became the active one before global isVRMode flips true. */
  onEnterVR?: () => void
  /** Fired right as VR is exited — lets a multi-video host restore whatever state
   *  (mute, playback) it suspended when this instance entered VR. */
  onExitVR?: () => void
  /** Renders the collapsed entry button at the size of a small icon button and drops the
   *  detection banner. For hosts like a Gooncave grid cell, where the button sits in a row
   *  of 28px controls and there is no room for a banner. */
  compact?: boolean
}

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds)) return "0:00"
  const safe = Math.max(0, seconds)
  const m = Math.floor(safe / 60)
  return `${m}:${Math.floor(safe % 60).toString().padStart(2, "0")}`
}

export function VROverlay({
  videoRef,
  contentUrl,
  sourceId,
  syncDelay,
  onSyncDelayChange,
  activeScript,
  onScriptSelect,
  onScriptClear,
  onSeek,
  onPlayPause,
  onEnterVR,
  onExitVR,
  compact = false,
}: VROverlayProps) {
  const t = useTranslations("vrOverlay")
  const {
    isVRMode,
    projectionType,
    stereoLayout,
    passthrough,
    xrSupported,
    setVRMode,
    setXRSupported,
    setAudioAvailable,
    setProjectionType,
    setStereoLayout,
    saveVideoSettings,
    loadVideoSettings,
  } = useVRStore()

  const [controlsOpen, setControlsOpen] = useState(false)
  const [uiHidden, setUiHidden] = useState(false)
  const [immersivePresenting, setImmersivePresenting] = useState(false)
  const [playback, setPlayback] = useState({
    paused: true,
    currentTime: 0,
    duration: 0,
    volume: 1,
    muted: false,
    rate: 1,
  })
  const [scrubTime, setScrubTime] = useState<number | null>(null)
  const scrubTimeRef = useRef<number | null>(null)

  const [detection, setDetection] = useState<{
    projectionType: typeof projectionType
    stereoLayout: typeof stereoLayout
    confidence: "high" | "low"
  } | null>(null)
  const [detectionDismissed, setDetectionDismissed] = useState(false)

  useEffect(() => {
    let cancelled = false

    const refresh = () => {
      checkImmersiveVRSupport().then((supported) => {
        if (!cancelled) setXRSupported(supported)
      })
    }

    refresh()
    const removeListener = onXRDeviceChange(refresh)

    return () => {
      cancelled = true
      removeListener()
    }
  }, [setXRSupported])

  // Detect VR format from the content URL
  useEffect(() => {
    const detectionSource = sourceId || contentUrl
    if (!detectionSource) return
    setDetectionDismissed(false)
    const result =
      detectionSource.startsWith("blob:") || detectionSource.startsWith("file:")
        ? null
        : detectVRFromUrl(detectionSource) ?? detectVRFromFilename(detectionSource.split("/").pop() ?? "")
    setDetection(result)
  }, [contentUrl, sourceId])

  // Try loading per-video saved settings
  useEffect(() => {
    const settingsSource = sourceId || contentUrl
    if (!settingsSource) return
    const key = getVideoSettingsKey(settingsSource)
    loadVideoSettings(key)
  }, [contentUrl, sourceId, loadVideoSettings])

  // Save settings when exiting VR
  useEffect(() => {
    const settingsSource = sourceId || contentUrl
    if (!isVRMode && settingsSource) {
      const key = getVideoSettingsKey(settingsSource)
      saveVideoSettings(key)
    }
  }, [isVRMode, contentUrl, sourceId, saveVideoSettings])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const syncPlayback = (e?: Event) => {
      // The only thing that shows currentTime is the VR-mode control bar, so while VR is
      // closed a timeupdate is a pointless re-render (this effect re-runs and re-syncs on
      // isVRMode, so the value is fresh when VR opens).
      if (e?.type === "timeupdate" && !isVRMode) return
      setPlayback({
        paused: video.paused,
        currentTime: (scrubTimeRef.current ?? video.currentTime) || 0,
        duration: Number.isFinite(video.duration) ? video.duration : 0,
        volume: video.volume,
        muted: video.muted,
        rate: video.playbackRate,
      })
    }

    syncPlayback()
    video.addEventListener("play", syncPlayback)
    video.addEventListener("pause", syncPlayback)
    video.addEventListener("timeupdate", syncPlayback)
    video.addEventListener("durationchange", syncPlayback)
    video.addEventListener("volumechange", syncPlayback)
    video.addEventListener("ratechange", syncPlayback)
    return () => {
      video.removeEventListener("play", syncPlayback)
      video.removeEventListener("pause", syncPlayback)
      video.removeEventListener("timeupdate", syncPlayback)
      video.removeEventListener("durationchange", syncPlayback)
      video.removeEventListener("volumechange", syncPlayback)
      video.removeEventListener("ratechange", syncPlayback)
    }
  }, [videoRef, isVRMode])

  useEffect(() => {
    scrubTimeRef.current = null
    setScrubTime(null)
  }, [contentUrl])

  // Safety net for a stuck scrub. syncPlayback above reports `scrubTimeRef.current ??
  // video.currentTime`, so while that ref is set the time label and the seek handle are
  // pinned to the scrub position. It is otherwise only cleared by commitSeek — i.e. Radix's
  // onValueCommit — which does NOT fire reliably when the pointer is released off the track
  // or the interaction is cancelled, which is routine with a VR controller ray or touch.
  // Miss it once and the ref stays set forever: the video keeps playing but the UI is frozen,
  // and play/pause appears to do nothing. That is the reported "seeking breaks the video".
  // A window-level release guarantees the scrub always ends.
  const onSeekRef = useRef(onSeek)
  useEffect(() => { onSeekRef.current = onSeek }, [onSeek])
  const isScrubbing = scrubTime !== null
  useEffect(() => {
    if (!isScrubbing) return
    const release = () => {
      const video = videoRef.current
      const target = scrubTimeRef.current
      if (video && target !== null) {
        video.currentTime = target
        onSeekRef.current?.(target)
      }
      scrubTimeRef.current = null
      setScrubTime(null)
    }
    window.addEventListener("pointerup", release)
    window.addEventListener("pointercancel", release)
    return () => {
      window.removeEventListener("pointerup", release)
      window.removeEventListener("pointercancel", release)
    }
  }, [isScrubbing, videoRef])

  // vrXRStore is a plain zustand store (not a React-tree hook), so it's readable/
  // subscribable from this flat DOM component even though it lives outside <Canvas>/<XR>.
  // `session` only becomes non-null once the headset actually accepts the immersive
  // session — this is the one reliable signal for "truly presenting" vs. just having
  // clicked Enter VR (canAttemptImmersiveXR() can succeed and still not end in a session,
  // e.g. the user backs out of the permission prompt).
  useEffect(() => {
    let wasPresenting = vrXRStore.getState().session != null
    return vrXRStore.subscribe((state) => {
      const nowPresenting = state.session != null
      setImmersivePresenting(nowPresenting)
      // The session can end without our own exitVR() ever running — the headset's
      // system UI (Oculus button, "remove headset" auto-pause-then-timeout, etc.) can
      // end it directly. Without this, isVRMode would stay stuck true with uiHidden
      // still true from the effect below: a black screen with no controls, only
      // recoverable by reloading the page.
      // Dropping back to the flat player (not all the way out) is deliberate — see
      // exitImmersive()'s comment. A headset-initiated end (system button, auto-timeout)
      // should land you in the same place a manual "Exit" tap would.
      if (wasPresenting && !nowPresenting) {
        bypassVRAudio(videoRef.current)
        setControlsOpen(true)
        setUiHidden(false)
      }
      // T-audio (docs/VR_TASKS.md): activateVRAudio's AudioContext.resume() was only ever
      // called back at enterVR() (the flat 2D staging step) — never again when the headset
      // actually accepts the immersive session. Entering a real WebXR session is reported
      // to suspend an AudioContext that wasn't created/resumed as part of that same session
      // (Quest's Chromium enforces this; it isn't scoped to this app), which matches
      // "audio didn't play once I went into immersive" even though the 2D staging view
      // (which resumed it earlier, outside the session) was fine.
      if (!wasPresenting && nowPresenting) {
        activateVRAudio(videoRef.current, useVRStore.getState().audio)
      }
      wasPresenting = nowPresenting
    })
  }, [videoRef])

  // Once the headset accepts the session, the in-headset 3D menu (VRHeadsetMenu /
  // VRMiniTransport, see vr-headset-menu.tsx) is the real control surface — this flat
  // portal UI was staying mounted and open the entire time regardless, which is two
  // real problems at once: (1) it's what "the browser window is still up in VR" was —
  // a fixed-inset-0 flat DOM panel with no reason to still be there once truly
  // immersive, and (2) its syncPlayback effect above re-renders this whole tree (HUD,
  // playback bar, and the VR Controls sidebar with its many sliders) on every video
  // `timeupdate`, dozens of times a second — real main-thread contention against the
  // exact same rAF loop the WebXR frame depends on. Confirmed via live headset testing:
  // playback was ~6fps with this UI left open the whole session. Forcing it closed here
  // removes that entire subtree from doing any work while presenting; VRPlayer's Canvas
  // (and the in-headset menu inside it) keeps rendering regardless of uiHidden.
  useEffect(() => {
    if (immersivePresenting) {
      setControlsOpen(false)
      setUiHidden(true)
    }
  }, [immersivePresenting])

  const applyDetection = () => {
    if (!detection) return
    setProjectionType(detection.projectionType)
    setStereoLayout(detection.stereoLayout)
    setDetectionDismissed(true)
  }

  // Opens the flat 2D player — video + flat playback bar + VR Controls sidebar — WITHOUT
  // requesting an immersive session. This used to happen in one step (this function used to
  // also call vrXRStore.enterVR() immediately), which meant there was no way to look at or
  // adjust anything before being dropped straight into the headset. Now that's a deliberate
  // second step — see enterImmersive() below, triggered by its own button in this view.
  const enterVR = () => {
    onEnterVR?.()
    const video = videoRef.current
    setAudioAvailable(video ? activateVRAudio(video, useVRStore.getState().audio) : null)
    setVRMode(true)
    setControlsOpen(true)
    setUiHidden(false)
    videoRef.current?.play().catch(() => {})
  }

  const enterImmersive = () => {
    if (!((passthrough || xrSupported !== false) && canAttemptImmersiveXR())) return
    // The <Canvas>/<XR> that owns vrXRStore's WebXR manager is only mounted once enterVR()'s
    // setVRMode(true) has rendered the portal — on a very first open in the same tick as that,
    // the first attempt(s) reject with "not connected to three.js. You either might be missing
    // the <XR> component or the canvas is not yet loaded?" (reproduced on desktop, and the
    // exact error seen in-headset). Retrying briefly until the Canvas connects fixes it; 40 x
    // 50ms = 2s stays well inside the browser's transient-user-activation window that
    // requestSession() needs. Harmless now that this is its own explicit button press (a real
    // click always has fresh activation), but cheap enough to keep as a safety net.
    const requestImmersive = async (attempt = 0): Promise<void> => {
      try {
        const enter = passthrough ? vrXRStore.enterAR : vrXRStore.enterVR
        const session = await enter.call(vrXRStore)
        if (session) setXRSupported(true)
      } catch (error: unknown) {
        const notConnectedYet = error instanceof Error && error.message.startsWith("not connected to three.js")
        if (notConnectedYet && attempt < 40 && useVRStore.getState().isVRMode) {
          await new Promise((resolve) => setTimeout(resolve, 50))
          return requestImmersive(attempt + 1)
        }
        console.error("Failed to enter immersive WebXR", error)
      }
    }
    void requestImmersive()
  }

  // Ends the immersive session but keeps the flat 2D player open — "sometimes you want to just
  // exit it so you can fix settings" was the explicit ask. This is what the in-headset Exit VR
  // button (VRXRUi) calls, and what fires automatically if the session ends from outside (the
  // subscribe effect above). To leave the player entirely, see closePlayer().
  const exitImmersive = () => {
    try { vrXRStore.getState().session?.end() } catch {}
    bypassVRAudio(videoRef.current)
    setControlsOpen(true)
    setUiHidden(false)
  }

  // Leaves the VR player entirely, back to the collapsed Enter VR badge (or, for a host like
  // the booru modal, back to the normal post view). Ends any immersive session first.
  const closePlayer = () => {
    try { vrXRStore.getState().session?.end() } catch {}
    bypassVRAudio(videoRef.current)
    setVRMode(false)
    setControlsOpen(false)
    setUiHidden(false)
    onExitVR?.()
  }

  const hideUI = () => {
    setControlsOpen(false)
    setUiHidden(true)
  }

  const showControls = () => {
    setUiHidden(false)
    setControlsOpen(true)
  }

  const togglePlayback = () => {
    // Routed through the host's real toggle when given — that's what starts/stops device
    // script sync (playDeviceScript/pauseDeviceScript in app/vr/page.tsx). Playing/pausing
    // the video element directly here bypassed that entirely.
    if (onPlayPause) {
      onPlayPause()
      return
    }
    const video = videoRef.current
    if (!video) return
    if (video.paused) video.play().catch(() => {})
    else video.pause()
  }

  const clampSeekTime = (requested: number) => {
    const video = videoRef.current
    const duration = video && Number.isFinite(video.duration) ? video.duration : playback.duration
    return Math.max(0, Math.min(duration || 0, requested))
  }

  // Radix fires onValueChange on every pointermove tick during a drag — every tick was
  // reassigning video.currentTime (a real decode/seek, not free) AND setPlayback (a second,
  // redundant re-render on top of the one setScrubTime already causes; the slider's own
  // position and the time label both only ever need scrubTime during a drag — see the JSX).
  // Together that was enough main-thread work per tick to visibly compete with the drag
  // itself: the handle would take one tick's worth of movement, then stall while everything
  // caught up. The handle/label still update on every tick (setScrubTime, cheap), but the
  // actual video seek is throttled — good enough to scrub-preview frames without decoding
  // every single intermediate position. commitSeek (on release) always seeks to the exact
  // final value regardless of the throttle.
  const lastRealSeekAt = useRef(0)
  const previewSeek = (value: number[]) => {
    const video = videoRef.current
    if (!video) return
    const next = clampSeekTime(value[0] ?? 0)
    scrubTimeRef.current = next
    setScrubTime(next)
    const now = performance.now()
    if (now - lastRealSeekAt.current >= 80) {
      lastRealSeekAt.current = now
      video.currentTime = next
    }
  }

  const commitSeek = (value: number[]) => {
    const video = videoRef.current
    if (!video) return
    const next = clampSeekTime(value[0] ?? scrubTimeRef.current ?? 0)
    video.currentTime = next
    setPlayback((current) => ({ ...current, currentTime: next }))
    onSeek?.(next)
    scrubTimeRef.current = null
    setScrubTime(null)
  }

  const skipBy = (seconds: number) => {
    const video = videoRef.current
    if (!video) return
    const duration = Number.isFinite(video.duration) ? video.duration : playback.duration
    const next = Math.max(0, Math.min(duration || 0, video.currentTime + seconds))
    video.currentTime = next
    setPlayback((current) => ({ ...current, currentTime: next }))
    onSeek?.(next)
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
  }

  const setVolume = (value: number[]) => {
    const video = videoRef.current
    if (!video) return
    const next = Math.max(0, Math.min(1, value[0] ?? 0))
    video.volume = next
    video.muted = next === 0
  }

  const cycleRate = () => {
    const video = videoRef.current
    if (!video) return
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2]
    const next = rates[(rates.indexOf(video.playbackRate) + 1) % rates.length] ?? 1
    video.playbackRate = next
  }

  if (!isVRMode) {
    // ── Collapsed: just show the Enter VR button + optional detection banner
    return (
      <div
        className={compact ? "contents" : "relative flex flex-col"}
        // A host embedding this inline among its own clickable controls (e.g. a Gooncave
        // grid cell's hover pill) needs this click to not also trigger ITS click handler.
        // This has to live on VROverlay's own root, not a wrapper div the host adds around
        // it — an extra ancestor can become the containing block for the fixed-position
        // immersive view below once isVRMode flips true (a `display: contents` ancestor in
        // particular is a known trigger for this), silently shrinking "cover the viewport"
        // down to "cover that wrapper's box" instead. onClick (bubble phase, not capture)
        // still runs after this component's own button handler already fired.
        onClick={(e) => e.stopPropagation()}
      >
        {/* Detection banner — absolutely positioned, NOT in normal flow, on purpose: this
            used to be a flex sibling of the Enter VR button below, so whenever it appeared
            it pushed that button down by its own height. Floating it above the button
            instead means the button never moves regardless of whether detection ran or
            found anything. Background is a real near-opaque white/light gray, not the
            muted/50 translucent gray it had before — that let enough of the busy video
            behind it bleed through to fight the text for contrast; a solid light backdrop
            with dark text reads cleanly regardless of what's playing behind it. */}
        {!compact && detection && !detectionDismissed && (
          // flex-nowrap + truncate (not flex-wrap + a basis-48 minimum on the text span)
          // on purpose — the old version wrapped into a tall vertical stack (icon on its
          // own line, three lines of text, buttons on a fourth) in anything narrower than
          // ~520px, which is most of where this actually renders. This forces one short
          // horizontal strip always: the label truncates with an ellipsis in the rare case
          // it's genuinely too tight, rather than the whole thing growing tall.
          <div className="absolute bottom-full left-0 mb-2 flex w-max max-w-[min(420px,calc(100vw-1rem))] flex-nowrap items-center gap-1.5 rounded-md border border-black/10 bg-neutral-50/95 px-2.5 py-1.5 text-xs text-neutral-900 shadow-lg backdrop-blur-sm">
            <Glasses className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="min-w-0 flex-1 truncate">
              {t.rich("detectedProjectionStereo", { projection: projectionLabel(detection.projectionType), stereo: stereoLabel(detection.stereoLayout), b: (chunks) => <strong>{chunks}</strong> })}
              {detection.confidence === "low" && (
                <span className="text-neutral-500"> {t("lowConfidence")}</span>
              )}
            </span>
            <Button size="sm" variant="default" className="h-6 shrink-0 text-xs px-2" onClick={applyDetection}>
              {t("apply")}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0 text-neutral-900 hover:bg-black/10"
              onClick={() => setDetectionDismissed(true)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Bumped from h-7/w-3.5 icon (matches the page's other small toolbar
            buttons) to a filled, colored button on its own — reported live as
            "too lowkey and small" to notice as the actual entry point into VR,
            the main reason this component exists at all. It's absolutely
            positioned on its own (see app/vr/page.tsx), not sharing a toolbar
            row with anything else, so this doesn't clash with a sibling
            button's sizing the way the reload/tab buttons do. h-11 (44px) was
            later reported as overcorrecting — too big/heavy next to the rest
            of the toolbar — h-9 (36px) is the current middle ground. */}
        <Button
          variant="default"
          size="icon"
          className={
            compact
              ? "h-[34px] w-[34px] max-[420px]:h-[30px] max-[420px]:w-[30px] rounded-full shrink-0 shadow-none"
              : "h-12 w-12 rounded-full shadow-lg hover:scale-105 transition-transform"
          }
          title={t("enterVRMode")}
          onClick={enterVR}
        >
          <Glasses className={compact ? "size-[18px]" : "size-7"} />
        </Button>
      </div>
    )
  }

  // ── Active VR mode: full-screen canvas + slide-in controls panel
  //
  // Rendered via a portal straight to document.body, NOT in place — this has to cover the
  // real viewport regardless of where the trigger button that opened it happens to live in
  // the tree. A Gooncave grid cell's hover controls, for instance, sit inside a div that
  // toggles `opacity-0`/`opacity-100` on hover (components/gooncave.tsx's cellOverlayOpen
  // pill). Any CSS opacity value other than 1 makes an element a containing block for its
  // fixed-position descendants — the same category as transform/filter/perspective — so
  // once the cursor left that cell (opacity back to 0), "fixed inset-0" silently became
  // "cover that cell's own small box" instead of the screen, showing the grid bleeding
  // through around/behind the video. A portal sidesteps this for good: no matter what CSS
  // any future caller's ancestor tree does, this only ever sits under <body>.
  return createPortal(
    // pointer-events-auto is required, not decorative: when this is opened from a host that
    // sits inside a modal Radix Dialog (the booru detail modal), Radix sets
    // `body { pointer-events: none }` for as long as the dialog is open. This portal lives
    // directly under <body>, outside the DialogContent, so it inherited that "none" —
    // every control in it was dead except the few HUD buttons that individually set
    // pointer-events-auto (verified live: body computed pointer-events is "none" with the
    // modal open). Restoring it on the portal root fixes the whole subtree at once.
    <div className="pointer-events-auto fixed inset-0 z-[60] flex bg-black" onClick={(e) => e.stopPropagation()}>
      {/* Three.js VR canvas */}
      <div className="relative min-w-0 flex-1">
        <VRPlayer
          videoElement={videoRef.current ?? null}
          className="w-full h-full"
          syncDelay={syncDelay}
          onSyncDelayChange={onSyncDelayChange}
          onToggleControls={showControls}
          activeScript={activeScript}
          onScriptSelect={onScriptSelect}
          onScriptClear={onScriptClear}
          onSeek={onSeek}
          onPlayPause={onPlayPause}
          onExit={exitImmersive}
        />

        {/* Top-bar HUD */}
        {!uiHidden && (
        <div className="absolute top-0 left-0 right-0 flex flex-wrap items-start justify-between gap-2 px-4 py-3 bg-gradient-to-b from-black/70 to-transparent pointer-events-none z-10">
          <div className="flex min-w-0 items-center gap-2 pointer-events-auto">
            <Badge variant="secondary" className="h-auto max-w-[60vw] gap-1 whitespace-normal text-xs">
              <Glasses className="h-3 w-3" />
              {projectionLabel(projectionType)} · {stereoLabel(stereoLayout)}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 pointer-events-auto">
            {/* The actual "put the headset on" step — deliberately its own button, not
                automatic on opening the player (see enterImmersive()'s comment). Styled as
                the primary action here since it's the main reason this view exists. */}
            {!immersivePresenting && (
              <Button
                variant="default"
                className="h-8 gap-1.5 px-3 text-xs font-medium"
                onClick={enterImmersive}
                title={t("enterVRMode")}
              >
                <Glasses className="h-3.5 w-3.5" />
                {t("enterVRMode")}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={showControls}
              title={t("openVRControls")}
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={hideUI}
              title={t("hideVRUI")}
            >
              <EyeOff className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={closePlayer}
              title={t("exitVR")}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        )}

        {/* Drag-to-look hint */}
        {!uiHidden && xrSupported !== true && (
          <div className="absolute bottom-32 left-1/2 -translate-x-1/2 text-xs text-white/50 pointer-events-none select-none">
            {t("dragToLookAroundScrollToZoom")}
          </div>
        )}

        {!uiHidden && (
          <div className="absolute bottom-4 left-4 right-4 z-10 mx-auto flex max-w-xl flex-col gap-2 rounded-lg border border-white/10 bg-black/70 p-3 text-white shadow-xl backdrop-blur-md">
            <div className="flex min-w-0 items-center gap-3">
              <span className="w-10 shrink-0 text-right text-xs tabular-nums text-white/70">{formatTime(scrubTime ?? playback.currentTime)}</span>
              <Slider
                value={[Math.min(scrubTime ?? playback.currentTime, playback.duration || scrubTime || playback.currentTime)]}
                max={playback.duration || 1}
                step={0.1}
                onValueChange={previewSeek}
                onValueCommit={commitSeek}
                className="min-w-0 flex-1"
              />
              <span className="w-10 shrink-0 text-xs tabular-nums text-white/70">{formatTime(playback.duration)}</span>
            </div>
            {/* justify-center (not justify-between) on purpose: this row sits between the
                video pane and the slide-in side panel, whose combined width can squeeze the
                video down to a sliver — narrow enough that flex-wrap puts playback and volume
                on separate lines. justify-between only pushes items apart when they share a
                line; with one group per line it collapses to "start", which is why both rows
                hugged the left edge instead of being centered. justify-center centers each
                wrapped line individually, and still reads fine as one row at full width. */}
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-9 w-9 text-white hover:bg-white/20" onClick={() => skipBy(-10)} title={t("back10Seconds")}>
                  <SkipBack className="h-4 w-4" />
                </Button>
                <Button size="icon" className="h-10 w-10 rounded-full" onClick={togglePlayback} title={playback.paused ? "Play" : "Pause"}>
                  {playback.paused ? <Play className="h-4 w-4 ml-0.5" /> : <Pause className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-9 w-9 text-white hover:bg-white/20" onClick={() => skipBy(10)} title={t("forward10Seconds")}>
                  <SkipForward className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="ghost" size="sm" className="h-9 px-2 text-xs text-white hover:bg-white/20" onClick={cycleRate} title={t("playbackSpeed")}>
                {playback.rate}x
              </Button>
              <div className="flex min-w-32 items-center gap-2">
                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-white hover:bg-white/20" onClick={toggleMute} title={playback.muted ? "Unmute" : "Mute"}>
                  {playback.muted || playback.volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>
                <Slider
                  value={[playback.muted ? 0 : playback.volume]}
                  max={1}
                  step={0.05}
                  onValueChange={setVolume}
                  className="w-28"
                />
              </div>
            </div>
          </div>
        )}

        {/* Toggle tab when controls closed */}
        {!uiHidden && !controlsOpen && (
          // Deliberately chunky: this is the one way back to the controls once they're
          // closed, and at p-1.5 with a 16px chevron it was a ~28px target — fine for a
          // mouse, genuinely hard to hit with a controller ray in the headset. Now a tall
          // grab-bar (w-9, h-24) with a visible edge, kept translucent so it stays out of
          // the way of the video rather than reading as a solid chrome element.
          <button
            type="button"
            aria-label={t("openVRControls")}
            className="absolute right-0 top-1/2 z-10 flex h-24 w-9 -translate-y-1/2 items-center justify-center rounded-l-lg border-y border-l border-white/20 bg-black/55 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white"
            onClick={showControls}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}

        {uiHidden && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="absolute right-3 top-3 z-10 h-auto min-h-9 max-w-[calc(100vw-1.5rem)] gap-2 rounded-full bg-black/65 px-3 py-2 text-xs text-white shadow-lg backdrop-blur hover:bg-black/80"
            onClick={showControls}
          >
            <Eye className="h-4 w-4 shrink-0" />
            {t("showUI")}
          </Button>
        )}
      </div>

      {/* Slide-in controls panel — forced dark + glassmorphic (not bg-background, which
          follows the app's light/dark theme setting) on purpose. This sits directly beside
          a black immersive video regardless of what theme the rest of the app is in, and a
          bright white panel there is genuinely disorienting in VR ("flashbanged" was the
          reported description) — so this one surface never follows the app theme; it's
          always dark, to match the bottom playback bar's existing bg-black/70 backdrop-blur
          look and the in-headset 3D menu's own dark canvas theme (vr-headset-menu.tsx),
          so the same "VR Controls" surface looks the same wherever it's actually reachable.
          The `dark` class scopes every themed color inside (VRControls' own buttons, inputs,
          accordions, etc. all read CSS custom properties that `.dark` in globals.css
          redefines) without needing to touch that file at all. */}
      {!uiHidden && controlsOpen && (
        <div
          className="dark relative w-[clamp(20rem,34vw,30rem)] max-w-[calc(100vw-2rem)] min-w-0 select-none bg-black/55 backdrop-blur-xl border-l border-white/10 flex flex-col text-white overflow-hidden"
          style={{
            // Scoped overrides, not global theme edits — only this panel needs them.
            // --muted-foreground (labels + values throughout VRControls, e.g. "Roll" and
            // "0°") was the app's default dark-theme value, oklch(0.6 0 0) — legible on an
            // opaque dark card, too dim once the panel itself went translucent/glass at
            // bg-black/55 instead of a near-opaque bg-black/80.
            "--muted-foreground": "oklch(0.82 0 0)",
            // --muted is the section-header ("View Transform", "Optics & Stereo", ...) tab
            // background. It's a couple percent lighter here so those read as distinct
            // clickable surfaces against the panel instead of blending into it.
            "--muted": "oklch(0.32 0.02 283 / 0.75)",
          } as CSSProperties}
        >
          {/* Glass sheen — a flat translucent black is dark, but doesn't actually read as
              glass. A faint diagonal highlight is the one cheap thing that reads as "glass"
              rather than "dark panel"; kept subtle and pointer-events-none since it's purely
              decorative and sits over interactive content. */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 25%, rgba(255,255,255,0) 50%)" }}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-white/10">
            <span className="font-semibold text-sm">{t("vRControls")}</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-white/10"
                onClick={hideUI}
                title={t("hideAllVRUI")}
              >
                <EyeOff className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setControlsOpen(false)}
                title={t("collapseControls")}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {/* dragToScroll is deliberately OFF here for now. Grabbing anywhere on the panel
                to scroll competes with every control inside it for the same pointerdown —
                it kept stealing drags from the sliders (they needed repeated clicks before
                they'd take, and track clicks scrolled instead of setting a value). Needs a
                dedicated grip strip rather than the whole panel surface before it comes back.
                wheelAssist IS on — reported as "hover + scroll doesn't work" here specifically,
                likely from a gamepad-driven cursor whose scroll doesn't dispatch a real native
                wheel event the way a mouse does. This drives scrollTop directly off the wheel
                event instead of depending on the browser's own wheel-to-scroll handling. */}
            <ScrollArea className="h-full" wheelAssist>
              <div className="p-4 pb-5">
                <VRControls
                  syncDelay={syncDelay}
                  onSyncDelayChange={onSyncDelayChange}
                  activeScript={activeScript}
                  onScriptSelect={onScriptSelect}
                  onScriptClear={onScriptClear}
                />
              </div>
            </ScrollArea>
          </div>
          <div className="border-t px-4 py-3 shrink-0">
            <Button variant="outline" size="sm" className="h-auto min-h-9 w-full gap-2 whitespace-normal" onClick={closePlayer}>
              <X className="h-3.5 w-3.5" />
              {t("exitVR")}
            </Button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  )
}
